import { useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
// eslint-disable-next-line no-restricted-imports -- TODO: update to `next/navigation` before using this component in App Router
import { useRouter } from "next/router";
import { stringify } from "qs";

import { clearKeysFromQuery } from "@/hooks/useClearQueryStringKeys";
import useParsedQueryString from "@/hooks/useParsedQueryString";
import { useSelector } from "@/redux/hooks";
import { getIsDelta9Allowed } from "@/redux/selectors/complianceRules";
import toggleURLSearchParamValue from "@/utils/toggleURLSearchParamValue";

import FilterShortcut from "@/components/botanic/FilterShortcut";
import { InformationalModal } from "@/components/botanic/Modal";
import { FilterButton } from "@/components/FilteredGrid/FilterButton";
import { MenuFilterGroupTypes } from "@/components/MenuFilters/types/MenuFilters";

import AppliedChips from "../AppliedChips";
import { AvailableFilter, FilterType } from "../DynamicFilters/types";

export const FULFILLMENT_METHODS = [
  { name: "Direct Shipping", value: "shipping" },
  { name: "Pickup", value: "pickup" },
  { name: "Delivery", value: "delivery" },
];

const MobileFilterModalAndShortcutsUI = ({
  availableFilters,
  filtersUI,
  setOpenFilterSection,
}: {
  availableFilters: AvailableFilter[];
  filtersUI: React.ReactNode;
  setOpenFilterSection: (filterName?: string) => void;
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const targetURLSearchParams = new URLSearchParams(searchParams?.toString());
  // * Remove pagination params when clicking fulfillment filter link.
  targetURLSearchParams.delete("page");

  const { asPath, push } = useRouter();
  const [url, _hash] = asPath.split("#");
  const [path, _query] = url.split("?");

  const query = useParsedQueryString();
  const [modalActive, setModalActive] = useState(false);
  const modalButtonRef = useRef(null);
  // * In states where Delta9 products are legal, we want to hide our direct shipping quick filter. (original ticket: https://leafly.atlassian.net/browse/SHH-1044)
  const hideShippingQuickFilter = useSelector(getIsDelta9Allowed);

  const { fulfillment, filter: queryFilters } = query;
  const queryStringFilters =
    typeof queryFilters == "object" && !Array.isArray(queryFilters)
      ? queryFilters
      : {};
  const numberOfFiltersSelected = Object.entries(queryStringFilters).length;
  const queryStringFulfillments =
    fulfillment && Array.isArray(fulfillment)
      ? fulfillment.filter((f) => typeof f === "string")
      : [];
  const nonAppliedFulfillmentMethods = FULFILLMENT_METHODS.filter(
    (method) => !queryStringFulfillments.includes(method.value),
  );
  const nonAppliedFilterCategories = availableFilters.filter(
    (available) => !queryStringFilters[available.name],
  );

  const clearOpenSection = () => {
    setOpenFilterSection(undefined);
  };

  return (
    <div className="block relative md:hidden">
      <>
        <div className="flex items-center overflow-x-auto overflow-y-hidden whitespace-nowrap pb-sm mb-xs gap-1 no-scrollbar fade-white-right">
          <FilterButton
            filterCount={numberOfFiltersSelected}
            onClick={() => {
              setModalActive(true);
            }}
            ref={modalButtonRef}
          />

          <AppliedChips availableFilters={availableFilters} />

          {nonAppliedFulfillmentMethods.map((fulfillment) =>
            hideShippingQuickFilter &&
            fulfillment.value === "shipping" ? null : (
              <MobileMenuQuickFilter
                key={fulfillment.value}
                text={fulfillment.name}
                type={MenuFilterGroupTypes.toggle}
                onClick={() =>
                  push({
                    pathname,
                    query: toggleURLSearchParamValue(
                      targetURLSearchParams,
                      "fulfillment",
                      fulfillment.value,
                      { forceArray: true },
                    ).toString(),
                  })
                }
              />
            ),
          )}

          {nonAppliedFilterCategories.length > 0
            ? nonAppliedFilterCategories.map((filter, i) => (
                <MobileMenuQuickFilter
                  key={`${filter.name}_${i}`}
                  text={filter.label}
                  type={filter.type as unknown as MenuFilterGroupTypes}
                  onClick={() => {
                    if (filter.type === FilterType.toggle) {
                      push({
                        pathname,
                        query: toggleURLSearchParamValue(
                          targetURLSearchParams,
                          `filter[${filter.name}]`,
                          "true",
                        ).toString(),
                      });
                    } else {
                      setOpenFilterSection(filter.name);
                      setModalActive(true);
                    }
                  }}
                />
              ))
            : null}
        </div>
        {modalActive && (
          <InformationalModal
            title={`Filters${
              numberOfFiltersSelected ? ` (${numberOfFiltersSelected})` : ""
            }`}
            returnFocusRef={modalButtonRef}
            onDismissed={() => {
              setModalActive(false);
              clearOpenSection();
            }}
            expository
          >
            <div className="relative -mx-lg h-[calc(100%+32px)] -mt-4 flex flex-col justify-between">
              <div>{filtersUI}</div>
              <div className="sticky -bottom-4 flex w-full p-lg bg-white border-t border-light-grey">
                <Link
                  href={{
                    pathname: path,
                    query: stringify(
                      clearKeysFromQuery(query, [
                        "filter",
                        "fulfillment",
                        "page",
                        "q",
                        "sort",
                      ]),
                    ),
                  }}
                  className="button button--text flex-shrink-0 px-xxl text-xs text-default leading-[34px]"
                >
                  clear all
                </Link>
                <button
                  className="button button--secondary"
                  onClick={() => {
                    setModalActive(false);
                    clearOpenSection();
                  }}
                >
                  confirm
                </button>
              </div>
            </div>
          </InformationalModal>
        )}
      </>
    </div>
  );
};

const MobileMenuQuickFilter: React.FC<{
  onClick: () => void;
  text: string;
  type: MenuFilterGroupTypes;
}> = ({ onClick, text, type }) => {
  const isToggleFilter = type === MenuFilterGroupTypes.toggle;

  return (
    <FilterShortcut
      showChevron={!isToggleFilter}
      text={text}
      onClick={onClick}
    />
  );
};

export default MobileFilterModalAndShortcutsUI;
